import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserDto } from '../api/auth';
import { authApi } from '../api/auth';
import { setAuthRedirectHandler } from '../api/client';

// Desktop (Electron): VITE_API_URL is always set. Cookies don't work cross-origin without
// HTTPS, so the desktop path stores the token in sessionStorage and sends it as Bearer.
const IS_DESKTOP = !!import.meta.env.VITE_API_URL;

interface AuthContextValue {
  user: UserDto | null;
  token: string | null;
  isAuthenticated: boolean;
  isCheckingAuth: boolean;
  login: (token: string, user: UserDto) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const CHECK_INTERVAL_MS = 60_000;        // check every minute

function loadUser(): UserDto | null {
  try { return JSON.parse(sessionStorage.getItem('cl_user') ?? 'null') as UserDto | null; }
  catch { return null; }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    IS_DESKTOP ? sessionStorage.getItem('cl_token') : null,
  );
  const [user, setUser] = useState<UserDto | null>(loadUser);
  // True only while the web (cookie-based) /auth/me check below is in flight. A fresh browser
  // context (new tab, or the app reopened after a restart) has no sessionStorage-cached user yet
  // even though the session cookie itself may still be perfectly valid — Protected (App.tsx) must
  // wait for this to resolve before deciding to redirect to /login, or it redirects prematurely
  // and never recovers once /auth/me does come back successful.
  const [isCheckingAuth, setIsCheckingAuth] = useState(!IS_DESKTOP);
  const lastActivity = useRef(Date.now());
  const navigate = useNavigate();

  // Registers an in-app navigate for client.ts to call on a 401, instead of
  // a raw window.location assignment (see setAuthRedirectHandler for why).
  useEffect(() => {
    setAuthRedirectHandler((path) => navigate(path, { replace: true }));
    return () => setAuthRedirectHandler(null);
  }, [navigate]);

  // On web (cookie-based): if we have cached user info but no token in state,
  // verify the cookie is still valid via /me. This covers page refreshes.
  useEffect(() => {
    if (IS_DESKTOP) return;
    if (user) { setIsCheckingAuth(false); return; } // already authenticated
    authApi.me().then(u => {
      sessionStorage.setItem('cl_user', JSON.stringify(u));
      setUser(u);
    }).catch(() => {
      // No valid cookie — stay unauthenticated
      sessionStorage.removeItem('cl_user');
    }).finally(() => setIsCheckingAuth(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    if (IS_DESKTOP) {
      sessionStorage.removeItem('cl_token');
    } else {
      authApi.logout().catch(() => { /* best-effort cookie clear */ });
    }
    sessionStorage.removeItem('cl_user');
    setToken(null);
    setUser(null);
  }, []);

  const login = useCallback((t: string, u: UserDto) => {
    if (IS_DESKTOP) {
      sessionStorage.setItem('cl_token', t);
    }
    sessionStorage.setItem('cl_user', JSON.stringify(u));
    lastActivity.current = Date.now();
    setToken(IS_DESKTOP ? t : null);
    setUser(u);
  }, []);

  useEffect(() => {
    const isAuthed = IS_DESKTOP ? !!token : !!user;
    if (!isAuthed) return;
    const bump = () => { lastActivity.current = Date.now(); };
    window.addEventListener('mousemove', bump);
    window.addEventListener('keydown', bump);
    window.addEventListener('click', bump);
    window.addEventListener('scroll', bump, true);
    const timer = setInterval(() => {
      if (Date.now() - lastActivity.current > IDLE_TIMEOUT_MS) logout();
    }, CHECK_INTERVAL_MS);
    return () => {
      window.removeEventListener('mousemove', bump);
      window.removeEventListener('keydown', bump);
      window.removeEventListener('click', bump);
      window.removeEventListener('scroll', bump, true);
      clearInterval(timer);
    };
  }, [token, user, logout]);

  const isAuthenticated = IS_DESKTOP ? !!token : !!user;

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isCheckingAuth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export function useRole() {
  const { user } = useAuth();
  const role = user?.role ?? 'staff';
  return {
    role,
    canDelete: role === 'owner' || role === 'admin',
    canManageUsers: role === 'owner' || role === 'admin',
    isSuperAdmin: user?.isSuperAdmin ?? false,
  };
}
